import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import './index.less'

interface DropdownItem {
  label: string
  path?: string
  value?: string
}

interface NavDropdownProps {
  label: string
  items: DropdownItem[]
  onItemClick?: () => void
  onSelect?: (item: DropdownItem) => void
  selectedValue?: string
  align?: 'left' | 'right'
  compact?: boolean
  openOnHover?: boolean
  menuTheme?: 'gray' | 'white'
}

const NavDropdown = ({
  label,
  items,
  onItemClick,
  onSelect,
  selectedValue,
  align = 'left',
  compact = false,
  openOnHover = true,
  menuTheme = 'gray',
}: NavDropdownProps) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleItemClick = (item: DropdownItem) => {
    if (item.path) {
      navigate(item.path)
    }
    onSelect?.(item)
    setOpen(false)
    onItemClick?.()
  }

  return (
    <div
      className={`nav-dropdown ${compact ? 'nav-dropdown--compact' : ''}`}
      ref={ref}
      onMouseEnter={() => openOnHover && setOpen(true)}
      onMouseLeave={() => openOnHover && setOpen(false)}
    >
      <button
        className="nav-dropdown__trigger"
        onClick={() => setOpen(prev => !prev)}
      >
        {label}
        <span
          className={`nav-dropdown__arrow ${
            open ? 'nav-dropdown__arrow--open' : ''
          }`}
        >
          ▾
        </span>
      </button>
      <div
        className={`nav-dropdown__menu ${
          open ? 'nav-dropdown__menu--visible' : ''
        } ${align === 'right' ? 'nav-dropdown__menu--right' : ''} ${
          menuTheme === 'white' ? 'nav-dropdown__menu--white' : ''
        }`}
      >
        {items.map(item => (
          <button
            key={item.path || item.value || item.label}
            className={`nav-dropdown__item ${
              item.value && item.value === selectedValue
                ? 'nav-dropdown__item--active'
                : ''
            }`}
            onClick={() => handleItemClick(item)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export default NavDropdown
